import { useEffect, useState } from 'react';
import { Text, Icon, Label, PrimaryButton, Dropdown, ISelectableOption } from '@fluentui/react';
import TimeTaxSupportSystemClient from '../../../../system';
import { DateTime } from 'luxon';
import MessageCourier from '../../../../lib/MessageCourier';
import { getLocalizedStrings } from '../../../../lib/LocalizedStringsForDatePicker';
import CustomDatePicker from '../../../../lib/CustomDatePicker';
import { t } from 'i18next';
import { AgrupadorCorporacionLiquidadora } from '../../../../system/agrupador_corporacion_liquidadora/AgrupadorCorporacionLiquidadoraSystem';
import { getCorporativoInfo } from '../../../../lib/Util';

interface IProjectConfiguration {
  setButtonDisability: (can: boolean) => void;
  system: TimeTaxSupportSystemClient;
  getDataFromCompanySelectorAssistant: (month: DateTime) => void;
  courier: MessageCourier;
  hasItems: boolean | null;
  month: DateTime;
  lastSettlement: String | undefined;
  setSettlementType: React.Dispatch<React.SetStateAction<ISelectableOption<AgrupadorCorporacionLiquidadora>>>;
  settlementType: ISelectableOption<AgrupadorCorporacionLiquidadora>;
}

const PeriodReportHoursSettlement = (props: IProjectConfiguration) => {
  const [minDate2, setMinDate] = useState<Date | Date>(new Date());
  const [month, setMonth] = useState<DateTime>();
  const [agrupadoresCorporacionLiquidadora, setAgrupadoresCorporacionLiquidadora] = useState<{ agrcorliq_id: number; agrcorliq_nombre: string; agrcorliq_tipo: string; }[]>([]);

  useEffect(() => {
    const corporativoInfo = getCorporativoInfo();

    if (!corporativoInfo) return;

    const NRO_ADMIN_TRIBUTARIA_GE_ATIC_ESPANIA = 'A84570290';
    const NRO_ADMIN_TRIBUTARIA_GE_ATIC_PERU = '20451539087';

    if (corporativoInfo.empp_nro_admin_tributaria === NRO_ADMIN_TRIBUTARIA_GE_ATIC_ESPANIA) {
      setAgrupadoresCorporacionLiquidadora([
        {
          agrcorliq_id: 4,
          agrcorliq_nombre: 'ASESORIAS ESPAÑA',
          agrcorliq_tipo: 'ASESORIAS_ES',
        },
      ]);
    } else if (corporativoInfo.empp_nro_admin_tributaria === NRO_ADMIN_TRIBUTARIA_GE_ATIC_PERU) {
      setAgrupadoresCorporacionLiquidadora([
        {
          agrcorliq_id: 1,
          agrcorliq_nombre: 'AJEFILMS SERVICIOS',
          agrcorliq_tipo: 'AJE_FILMS_SERVICES',
        },
        {
          agrcorliq_id: 2,
          agrcorliq_nombre: 'AJEFILMS PROYECTOS',
          agrcorliq_tipo: 'AJE_FILMS_PROJECTS',
        },
        {
          agrcorliq_id: 3,
          agrcorliq_nombre: 'ASESORIAS PERU',
          agrcorliq_tipo: 'ASESORIAS_PE',
        },
      ]);
    } else {
      console.warn("El corporativo no pertenece a atic españa ni a atic peru: ", corporativoInfo)
    }
  }, []);

  useEffect(() => {
    if (month) {
      props.getDataFromCompanySelectorAssistant(month);
    }
  }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.hasItems) {
      props.setButtonDisability(true);
    }
  }, [props.hasItems]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    props.system
      .configuration()
      .then((dato) => {
        if (dato) {
          const dt = dato.getActivitiesBlocked();
          if (dt) {
            setMinDate(dt.toJSDate());
          } else {
            setMinDate(new Date());
          }
        }
      })
      .catch((error) => {
        console.error('Error al obtener la configuración del sistema:', error);
        setMinDate(new Date());
      });
  }, [props.system]);

  const onChangeDate = (date: Date | null | undefined, name: string) => {
    if (date) {
      setMonth(DateTime.fromJSDate(date));
    }
  };

  const getLastMonth = () => {
    if (props.lastSettlement) {
      const blockedDates: { [key: number]: number[] } = {};
      const dates = props.lastSettlement.split('|');

      dates.forEach((dateStr) => {
        const [year, month] = dateStr.split('-').map(Number);
        if (!blockedDates[year]) {
          blockedDates[year] = [];
        }
        blockedDates[year].push(month);
      });

      return blockedDates;
    }
    return {};
  };
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const onSelectDate = (date: Date | null | undefined) => {
    setIsDatePickerOpen(false);
    if (date) {
      onChangeDate(date, 'from');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const datePicker = document.querySelector('.ms-DatePicker');
      if (datePicker && !datePicker.contains(event.target as Node)) {
        if (isDatePickerOpen) {
          setIsDatePickerOpen(false);
          console.log('El DatePicker se ha cerrado');
        }
      }
    };

    if (isDatePickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDatePickerOpen]);

  return (
    <div>
      <div>
        <Text variant="smallPlus">
          <Icon iconName="Info" className="text-icon-small"></Icon>
          {t(
            'Para la generación del reporte, solo se registrará una nueva solicitud si no existe previamente una solicitud activa dentro del mismo mes.',
          )}
        </Text>
      </div>
      <div className="ms-Grid mt-2" dir="ltr">
        <div className="ms-Grid-row ">
          <div className="ms-Grid-col ms-sm3 ">
            {getLastMonth() ? (
              <CustomDatePicker
                strings={getLocalizedStrings()}
                label={t('Mes a generar')}
                value={props.month.toJSDate()}
                minDate={minDate2}
                blockedMonths={getLastMonth()}
                onSelectDate={onSelectDate}
              />
            ) : null}
          </div>
        </div>
        <div className="ms-Grid-row mt-2 mb-2">
          <div className="ms-Grid-col ms-sm3 ">
            <Dropdown
              label={t('Tipo de liquidación')}
              placeholder={t('Seleccione el tipo de liquidación')}
              options={agrupadoresCorporacionLiquidadora.map((a) => ({ key: a.agrcorliq_id, text: a.agrcorliq_nombre, data: a.agrcorliq_tipo }))}
              selectedKey={props.settlementType.key}
              onChange={(e, option) => {
                if (!option) return;
                props.setSettlementType(option);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeriodReportHoursSettlement;
